/**
 * Session 67 — Best-effort lead timezone derivation.
 *
 * Used by the manual-reminder schedule-send picker so the operator
 * sees what time the email will land in the **lead's** inbox. The
 * fallback chain is:
 *
 *   1. lead.timezone (IANA, set by an integration or the lead's reply)
 *   2. country = UK / United Kingdom → Europe/London
 *   3. country = US: state_region → mapped IANA
 *   4. default → America/New_York (USA Eastern, per user spec)
 *
 * State boundaries that span multiple timezones (FL, IN, KY, TN, TX,
 * MI, ID, NE, KS) are mapped to the *majority* zone — the zone the
 * state capital sits in. Operators on the edge can override via the
 * lead's `timezone` field.
 *
 * IANA names are stable across the browser tz database so
 * `Intl.DateTimeFormat({ timeZone: <name> })` always works.
 */

const US_STATE_TZ = {
  AL: "America/Chicago",   AK: "America/Anchorage",
  AZ: "America/Phoenix",   AR: "America/Chicago",
  CA: "America/Los_Angeles", CO: "America/Denver",
  CT: "America/New_York",  DE: "America/New_York",
  FL: "America/New_York",  GA: "America/New_York",
  HI: "Pacific/Honolulu",  ID: "America/Boise",
  IL: "America/Chicago",   IN: "America/Indiana/Indianapolis",
  IA: "America/Chicago",   KS: "America/Chicago",
  KY: "America/Kentucky/Louisville",
  LA: "America/Chicago",   ME: "America/New_York",
  MD: "America/New_York",  MA: "America/New_York",
  MI: "America/Detroit",   MN: "America/Chicago",
  MS: "America/Chicago",   MO: "America/Chicago",
  MT: "America/Denver",    NE: "America/Chicago",
  NV: "America/Los_Angeles", NH: "America/New_York",
  NJ: "America/New_York",  NM: "America/Denver",
  NY: "America/New_York",  NC: "America/New_York",
  ND: "America/Chicago",   OH: "America/New_York",
  OK: "America/Chicago",   OR: "America/Los_Angeles",
  PA: "America/New_York",  RI: "America/New_York",
  SC: "America/New_York",  SD: "America/Chicago",
  TN: "America/Chicago",   TX: "America/Chicago",
  UT: "America/Denver",    VT: "America/New_York",
  VA: "America/New_York",  WA: "America/Los_Angeles",
  WV: "America/New_York",  WI: "America/Chicago",
  WY: "America/Denver",    DC: "America/New_York",
};

// Full-state-name → 2-letter abbrev. Lets us derive a timezone for
// rows imported with "Utah" / "California" / "New York" in the
// `state_region` column instead of a postal code.
const US_STATE_NAME_TO_ABBR = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR",
  california: "CA", colorado: "CO", connecticut: "CT", delaware: "DE",
  florida: "FL", georgia: "GA", hawaii: "HI", idaho: "ID",
  illinois: "IL", indiana: "IN", iowa: "IA", kansas: "KS",
  kentucky: "KY", louisiana: "LA", maine: "ME", maryland: "MD",
  massachusetts: "MA", michigan: "MI", minnesota: "MN", mississippi: "MS",
  missouri: "MO", montana: "MT", nebraska: "NE", nevada: "NV",
  "new hampshire": "NH", "new jersey": "NJ", "new mexico": "NM",
  "new york": "NY", "north carolina": "NC", "north dakota": "ND",
  ohio: "OH", oklahoma: "OK", oregon: "OR", pennsylvania: "PA",
  "rhode island": "RI", "south carolina": "SC", "south dakota": "SD",
  tennessee: "TN", texas: "TX", utah: "UT", vermont: "VT",
  virginia: "VA", washington: "WA", "west virginia": "WV",
  wisconsin: "WI", wyoming: "WY",
  "district of columbia": "DC", "washington dc": "DC", "washington d.c.": "DC",
};

const US_NAMES = new Set(["us", "usa", "u.s.", "u.s.a.", "united states", "united states of america", "america"]);
const UK_NAMES = new Set(["uk", "u.k.", "gb", "gbr", "united kingdom", "great britain", "england", "scotland", "wales"]);

const DEFAULT_TZ = "America/New_York"; // user-specified fallback

/**
 * Resolve a lead's IANA timezone using the rules above.
 * Returns `{ tz, source, label }` so the caller can display
 * "Eastern Time (default — no address)" when the fallback kicks in.
 */
export function deriveLeadTimezone(lead) {
  if (!lead) return { tz: DEFAULT_TZ, source: "default", label: "Eastern Time (no lead)" };

  const explicit = (lead.timezone || "").trim();
  if (explicit) {
    return { tz: explicit, source: "explicit", label: explicit };
  }

  const country = (lead.country || "").trim().toLowerCase();
  const stateRaw = (lead.state_region || "").trim();
  const city = (lead.city || "").trim();

  if (UK_NAMES.has(country)) {
    return { tz: "Europe/London", source: "country", label: `London time${city ? " (" + city + ")" : ""}` };
  }

  if (US_NAMES.has(country) || (!country && stateRaw)) {
    // Try abbrev first, then full-name lookup.
    const stateUp = stateRaw.toUpperCase();
    let abbr = US_STATE_TZ[stateUp] ? stateUp : null;
    if (!abbr) {
      const fromFull = US_STATE_NAME_TO_ABBR[stateRaw.toLowerCase()];
      if (fromFull) abbr = fromFull;
    }
    if (abbr) {
      const tz = US_STATE_TZ[abbr];
      const cityLabel = city ? `${city}, ${abbr}` : `state of ${abbr}`;
      return { tz, source: "us-state", label: cityLabel };
    }
  }

  return {
    tz: DEFAULT_TZ,
    source: "default",
    label: "Eastern Time (no address — default)",
  };
}

/** Short timezone abbreviation (PST/PDT/EST/EDT/MST/MDT/...) for a given moment. */
export function getTzAbbrev(dt, tz) {
  return (
    new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" })
      .formatToParts(dt)
      .find((p) => p.type === "timeZoneName")?.value || ""
  );
}

/** Format `dt` as a short date+time in the given IANA timezone. */
export function formatInTz(dt, tz) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    dateStyle: "short",
    timeStyle: "short",
  }).format(dt);
}
